<template>
  <div ref="rootRef" class="w-full bg-gray-100 shadow-sm rounded-xl">
    <div class="flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-3">
      <!-- Logo -->
      <div class="flex items-center space-x-2 cursor-pointer select-none" @click="goHome">
        <img src="@/assets/args-parrot.svg" alt="Logo" class="h-12 w-12" />
        <!-- <img src="@/assets/parrot_pirate_transparent.png" alt="Logo" class="h-12 w-12" /> -->
        <span class="hidden sm:inline text-2xl font-bold">args</span>
      </div>

      <!-- Autocomplete Input -->
      <div class="w-full max-w-3xl flex-1">
        <div class="relative">
          <input
            :value="props.modelValue"
            @input="onInput"
            @focus="onFocus"
            @keydown.down.prevent="onArrowDown"
            @keydown.up.prevent="onArrowUp"
            @keydown.enter.prevent="onEnter"
            @keydown.esc.prevent="hideList"
            :placeholder="props.placeholder"
            class="w-full rounded-lg border border-gray-300 bg-white px-5 py-3 text-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            role="combobox"
            aria-haspopup="listbox"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-controls="listboxId"
            :aria-activedescendant="activeId"
          />
          <div v-if="loading" class="absolute right-3 top-2 text-xs text-gray-400">
            Loading…
          </div>

          <!-- Suggestion List -->
          <ul
            v-if="open && suggestions.length"
            :id="listboxId"
            role="listbox"
            class="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg"
          >
            <li
              v-for="(s, i) in suggestions"
              :key="s.claim_id + '-' + i"
              role="option"
              :id="optionId(i)"
              :aria-selected="i === highlighted"
              @mousedown.prevent="select(s)"
              @mouseenter="setHighlight(i)"
              class="px-3 py-2 cursor-pointer"
              :class="i === highlighted ? 'bg-indigo-50 text-indigo-700' : 'text-gray-900'"
            >
              <div class="font-medium break-words sm:truncate">
                {{ s.text }}
              </div>
              <!-- <div
                v-if="s.highlight?.length"
                class="text-xs text-gray-500 truncate"
              >
                {{ s.highlight}}
              </div> -->
            </li>
          </ul>
        </div>

        <!-- Error / No results -->
        <p v-if="error" class="mt-1 text-sm text-red-600">{{ error }}</p>
        <p
          v-else-if="open && !loading && !suggestions.length && (props.modelValue?.length ?? 0) >= props.minChars"
          class="mt-1 text-sm text-gray-500"
        >
          No results.
        </p>
      </div>
      <!-- Portal target for dev-option buttons -->
      <div id="claim-bar-actions" class="flex items-center gap-2 shrink-0"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import debounce from 'lodash.debounce'
import type { ClaimSearchHit, SearchResponse } from '../types/search'

// --- Props ---
const props = withDefaults(defineProps<{ 
  modelValue: string
  stance: string | null
  topic: string | null
  dataset: string | null
  placeholder?: string
  transform?: (resp: SearchResponse) => ClaimSearchHit[]
  minChars?: number
}>(), {
  minChars: 1
})

// --- Emits ---
const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'select', payload: { value: string; label: string; item: ClaimSearchHit }): void
  (e: 'confirm'): void
}>()

function goHome() {
  window.location.href = import.meta.env.BASE_URL
}

// --- Local State ---
const rootRef = ref<HTMLElement | null>(null)
const open = ref(false)
const suggestions = ref<ClaimSearchHit[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const highlighted = ref<number | null>(null)
const listboxId = ref('claim-listbox')
const activeId = ref('')

// --- Methods ---
function onInput(e: Event) {
  emit('update:modelValue', (e.target as HTMLInputElement).value)
}

function setHighlight(i: number) {
  highlighted.value = i
  activeId.value = optionId(i)
}

function onArrowDown() {
  if (!suggestions.value.length) return
  if (highlighted.value === null) {
    setHighlight(0)
  } else if (highlighted.value < suggestions.value.length - 1) {
    setHighlight(highlighted.value + 1)
  }
}

function onArrowUp() {
  if (!suggestions.value.length) return
  if (highlighted.value === null) {
    setHighlight(suggestions.value.length - 1)
  } else if (highlighted.value > 0) {
    setHighlight(highlighted.value - 1)
  }
}

function onEnter() {
  const idx = highlighted.value ?? 0
  const item = suggestions.value[idx]
  if (item) select(item)
  emit('confirm')
}

function hideList() {
  open.value = false
}


const justSelected = ref(false)

function select(item: ClaimSearchHit) {
  emit('select', {
    value: String(item.claim_id),
    label: item.text ?? (item as any).claim_text,
    item,
  })
  emit('update:modelValue', item.text ?? (item as any).claim_text ?? '')

  justSelected.value = true
  open.value = false
}

function optionId(id: string | number) {
  return `claim-option-${id}`
}

function onFocus() {
  fetchSuggestions(props.modelValue ?? '', true)
}

async function fetchSuggestions(query: string, ignoreMinChars = false) {
  if (!ignoreMinChars && query.length < props.minChars) {
    suggestions.value = []
    open.value = false
    return
  }

  loading.value = true
  error.value = null

  try {
    const API_BASE_URL = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '');  // remove trailing slash
    const url = new URL(`${API_BASE_URL}/query-autocomplete`, window.location.origin);
    url.searchParams.set('q', query)
    if (props.stance) url.searchParams.set('stance', props.stance)
    if (props.topic) url.searchParams.set('topic', props.topic)
    if (props.dataset) url.searchParams.set('dataset', props.dataset)

    const res = await fetch(url.toString(), {
      headers: { 'Accept': 'application/json' },
    })

    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data: SearchResponse = await res.json()

    suggestions.value = props.transform ? props.transform(data) : (data.hits as ClaimSearchHit[])
    open.value = true
    highlighted.value = null
    activeId.value = ''
  } catch (err) {
    console.error(err)
    error.value = 'Failed to load suggestions'
    suggestions.value = []
  } finally {
    loading.value = false
  }
}

// --- Click outside to close ---
const onClickOutside = (e: MouseEvent) => {
  if (rootRef.value && !rootRef.value.contains(e.target as Node)) {
    open.value = false
  }
}
onMounted(() => document.addEventListener('mousedown', onClickOutside))
onUnmounted(() => document.removeEventListener('mousedown', onClickOutside))

// --- Debounced fetch ---
const debouncedFetch = debounce((q: string) => {
  fetchSuggestions(q)
}, 250)

watch([() => props.modelValue, () => props.stance, () => props.topic, () => props.dataset], ([q]) => {
  if (justSelected.value) {
    justSelected.value = false
    return
  }
  debouncedFetch(q)
})
</script>
